import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { OrgUnitBasic } from 'app/core/models/org-unit/org-unit-search';
import { PersonBasic } from 'app/core/models/person/person-basic';
import { PersonDetailedPaged } from 'app/core/models/person/person-detailed';
import { PersonSearchParameters } from 'app/core/models/person/person-detailed-search';
import { PersonSearch } from 'app/core/models/person/person-search';
import { ConfigService } from 'app/core/services/config/config.service';
import { Observable, catchError, map, throwError } from 'rxjs';
import { OrgUnitService } from '../org-unit/org-unit.service';
import {PersonDataSearch} from "../../models/person/person-data-search";
import {Person} from "../../models/person/person";

export interface PersonJobRoles {
  personId: number;
  jobRoles: Array<{
    jobRoleDescription: string;
    jobRoleName: string;
    jobRoleId: number;
    orgUnitId: number;
    orgUnitName: string;
  }>;
}

export interface PersonVerificationAreas {
  personId: number;
  verificationAreas: Array<{
    funcAreaDescription: string;
    funcAreaId: number;
    funcAreaName: string;
    maxApprovalLevel: number;
  }>;
}

export interface PersonDirectReports {
  personId: number;
  directReports: Array<{
    personId: number;
    displayName: string;
    jobRoles: Array<{
      jobRoleId: number;
      jobRoleName: string;
    }>;
  }>;
}

export interface ExternalActivePerson {
  id: number;
  name: string;
  displayName: string;
  externalId: number;
  externalUserId: number;
  location: string;
  orgUnitNames: string;
  jobRoleNames: string;
}

export interface SessionAttendeePerson {
  id?: number;
  name?: string;
  displayName: string;
  givenName: string;
  familyName: string;
  externalId?: number;
  externalUserId: number;
  adUpn: string;
  externalSource?: string;
}

interface PersonJobRolesResponse {
  personJobRoles: PersonJobRoles;
}

export interface PersonLookup {
  hierarchyKey: string;
  key: string;
  name: string;
  parentKey: string;
}

export interface PersonSecrityRole {
  adUpn: string;
  securityRoleId: number;
  securityLevel: number;
  orgUnits: OrgUnitBasic[];
  id: number;
  name: string;
  displayName: string;
  externalId: number;
  externalUserId: string;
  isActive: boolean | null;
}

export interface SecurityRoleUpdate {
  securityRoleId: number;
  orgUnitIds: number[];
}

export interface PagedPersonSecurityRoleSearch {
  personSecurityRoles: {
    data: PersonSecrityRole[];
    page: number;
    pageSize: number;
    totalPageCount: number;
    totalRowCount: number;
  };
  parentOrgUnits: {
    [key: string]: OrgUnitBasic;
  };
}

export interface PersonSubordinate {
  displayName: string;
  id: number;
  jobRoles: { id: number; name: string }[];
}

const dotNetApiServerUri = environment.serverUrl;

@Injectable({
  providedIn: 'root',
})
export class PersonService {
  private http = inject(HttpClient);
  private config = inject(ConfigService);
  private orgUnitService = inject(OrgUnitService);

  updatePersonSecurityRole(
    personId: number,
    securityRoleUpdate: SecurityRoleUpdate,
  ) {
    const url = `${dotNetApiServerUri}/people/${personId}/securityRole`;
    return this.http.post(url, securityRoleUpdate).pipe(
      map((resp) => resp),
      catchError((err) => throwError(() => new Error(err))),
    );
  }

  personSearch(payload: PersonSearchParameters): Observable<PersonBasic[]> {
    const url = `${dotNetApiServerUri}/people/search`;
    return this.http.post<PersonBasic[]>(url, payload);
  }

  searchForSecurityRoles(
    payload: PersonSearchParameters,
  ): Observable<PersonSecrityRole[]> {
    const url = `${dotNetApiServerUri}/people/forSecurityRoles`;
    return this.http
      .post<PersonSecrityRole[]>(url, JSON.stringify(payload))
      .pipe(
        map((resp) => resp),
        catchError((err) => {
          return throwError(() => new Error(err));
        }),
      );
  }

  searchForSecurityRolesPaged(
    payload: PersonSearchParameters,
  ): Observable<PagedPersonSecurityRoleSearch> {
    const url = `${dotNetApiServerUri}/people/forSecurityRoles/paged`;
    return this.http
      .post<PagedPersonSecurityRoleSearch>(url, JSON.stringify(payload))
      .pipe(
        map((resp) => {
          return {
            ...resp,
            personSecurityRoles: {
              ...resp.personSecurityRoles,
              data: resp.personSecurityRoles.data.map((p) => {
                return {
                  ...p,
                  orgUnits: p.orgUnits.map((ou) => ({
                    ...ou,
                    ouHierarchy: this.orgUnitService.getHierarchyName(
                      ou,
                      resp.parentOrgUnits,
                    ),
                  })),
                };
              }),
            },
          };
        }),
      );
  }

  getPeopleDetailed(
    payload: PersonSearchParameters,
  ): Observable<PersonDetailedPaged> {
    const url = `${dotNetApiServerUri}/people/searchDetailed/paged`;
    return this.http.post<PersonDetailedPaged>(url, JSON.stringify(payload));
  }

  getExternalActivePeople(): Observable<ExternalActivePerson[]> {
    const url = `${dotNetApiServerUri}/people/externalActive`;
    return this.http.get<ExternalActivePerson[]>(url).pipe(
      map((resp) => resp),
      catchError((err) => {
        return throwError(() => new Error(err));
      }),
    );
  }

  getPersonLookup(payload: PersonSearchParameters): Observable<PersonLookup[]> {
    return this.http
      .post<
        PersonLookup[]
      >(`${dotNetApiServerUri}/people/lookups`, JSON.stringify(payload))
      .pipe(
        map((resp) => resp),
        catchError((err) => {
          return throwError(() => new Error(err));
        }),
      );
  }

  getPersonSearchPaged(
    payload: PersonSearchParameters,
  ): Observable<PersonSearch> {
    return this.http.post<PersonSearch>(
      `${dotNetApiServerUri}/people/search/paged`,
      payload,
    );
  }

  getPersonSubordinates(): Observable<PersonSubordinate[]> {
    return this.http.get<PersonSubordinate[]>(
      `${dotNetApiServerUri}/people/subordinates`,
    );
  }

  getPersonPhoto(
    size:
      | '48x48'
      | '64x64'
      | '96x96'
      | '120x120'
      | '240x240'
      | '360x360'
      | '432x432'
      | '504x504'
      | '648x648',
  ): Observable<Blob> {
    return this.http.get(`${dotNetApiServerUri}/people/photo/${size}`, {
      responseType: 'blob',
    });
  }

  searchPersonData(payload: PersonDataSearch): Observable<Person[]> {
    return this.http.post<Person[]>(
      `${dotNetApiServerUri}/data/Person`,
      payload
    );
  }

  addSessionAttendeePerson( payload: SessionAttendeePerson ): Observable<SessionAttendeePerson> {
    return this.http.post<SessionAttendeePerson>(
      `${dotNetApiServerUri}/people`,
      payload
    );
  }
}
